import { Box, Typography, CircularProgress } from '@mui/material';
import { DrawRequest } from '@models/drawRequests/drawRequest';
import DrawRequestCard from './DrawRequestCard';
import CustomPagination from '@components/sharedComponents/pagination/CustomPagination';
import { DRAW_REQUEST_PAGINATION_OPTIONS } from '@constants/drawManagerConstants';

interface DrawRequestCardListProps {
  drawRequests: DrawRequest[];
  totalCount: number;
  currentPage: number;
  onViewDrawRequest: (drawRequest: DrawRequest) => void;
  onPaginationChange: (newState: any) => void;
  getStatusChipColor: (
    status: number,
    theme: any
  ) => {
    label: string;
    bgColor: string;
    textColor: string;
    borderRadius: string;
  };
  rowsPerPage?: number;
  rowsPerPageOptions?: readonly number[];
  refetch?: () => void;
  sourceRecId?: string;
  isLoading?: boolean;
}

export default function DrawRequestCardList({
  drawRequests,
  totalCount,
  currentPage,
  onViewDrawRequest,
  onPaginationChange,
  getStatusChipColor,
  rowsPerPage = 10,
  rowsPerPageOptions = DRAW_REQUEST_PAGINATION_OPTIONS,
  refetch,
  sourceRecId,
  isLoading = false,
}: DrawRequestCardListProps) {
  const handlePaginationChange = ({
    page,
    rowsPerPage: newRowsPerPage,
  }: {
    page: number;
    rowsPerPage: number;
  }) => {
    onPaginationChange((prevState: any) => ({
      ...prevState,
      params: {
        ...prevState.params,
        page,
        rowsPerPage: newRowsPerPage,
      },
    }));
  };

  const renderDrawRequestCard = (drawRequest: DrawRequest) => (
    <DrawRequestCard
      key={drawRequest.id}
      drawRequest={drawRequest}
      onViewDrawRequest={onViewDrawRequest}
      getStatusChipColor={getStatusChipColor}
      refetch={refetch}
      sourceRecId={sourceRecId}
    />
  );

  const renderCardsList = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {drawRequests.map(renderDrawRequestCard)}
    </Box>
  );

  const renderPagination = () => (
    <CustomPagination
      count={totalCount}
      initialRowsPerPage={rowsPerPage}
      rowsPerPageOptions={rowsPerPageOptions as number[]}
      onStateChange={handlePaginationChange}
      disabled={drawRequests.length === 0}
      showRowsPerPage={true}
      currentPage={currentPage}
      aria-label="Draw requests pagination"
    />
  );

  const renderPaginationSection = () => totalCount > 0 && renderPagination();

  const renderEmptyState = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 2,
        textAlign: 'center',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 1,
          color: 'text.primary',
          fontWeight: 500,
        }}
      >
        No Draw Requests Found
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          maxWidth: '400px',
        }}
      >
        You haven't created any draw requests yet. Click "Request a Draw" to get
        started.
      </Typography>
    </Box>
  );

  const renderLoadingState = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 2,
      }}
    >
      <CircularProgress size={40} />
      <Typography
        variant="body2"
        sx={{
          mt: 2,
          color: 'text.secondary',
        }}
      >
        Loading draw requests...
      </Typography>
    </Box>
  );

  const renderMainContent = () => {
    if (isLoading) {
      return renderLoadingState();
    }

    if (totalCount === 0) {
      return renderEmptyState();
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {renderCardsList()}
        {renderPaginationSection()}
      </Box>
    );
  };

  return renderMainContent();
}
